import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import logo from '../assets/solidlabs-logo.png'
import { useT, useLanguage } from '../i18n/LanguageContext'

export default function Navbar() {
  const t = useT()
  const { language, setLanguage } = useLanguage()
  const { pathname } = useLocation()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Sekcje landingu adresujemy zawsze pełną ścieżką ('/#oferta'), nie samą
  // kotwicą. Dzięki temu ten sam link działa na stronie głównej i z podstrony
  // bloga — przewinięciem zajmuje się ScrollManager.
  const links = [
    { label: t.nav.about, to: '/#about' },
    { label: t.nav.product, to: '/#product' },
    { label: t.nav.oferta, to: '/#oferta' },
    { label: t.nav.realizacje, to: '/#realizacje' },
    { label: t.nav.blog, to: '/blog' },
    { label: t.nav.contact, to: '/#contact' },
  ]

  const isActive = (to: string) => to === '/blog' && pathname.startsWith('/blog')

  // Drawer zamyka się od razu po kliknięciu. Wcześniejszy wariant opóźniał
  // przewijanie o czas animacji — przy nawigacji routerem nie jest to potrzebne,
  // bo panel leży w `fixed` navie i jego zwijanie nie rusza układu dokumentu.
  const handleMobileLinkClick = () => setMobileOpen(false)

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#050507]/80 backdrop-blur-2xl border-b border-white/[0.06]'
          : 'bg-transparent'
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-6 flex items-center justify-between transition-all duration-500 ${
          scrolled ? 'h-20' : 'h-28'
        }`}
      >
        <Link to="/" aria-label={t.nav.logoAria} className="flex items-center group">
          <img
            src={logo}
            alt={t.nav.logoAlt}
            className={`w-auto brightness-0 invert opacity-90 group-hover:opacity-100 transition-all duration-500 ${
              scrolled ? 'h-14' : 'h-24'
            }`}
          />
        </Link>

        <div className="hidden md:flex items-center gap-1">
          {links.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              aria-current={isActive(link.to) ? 'page' : undefined}
              className={`px-4 py-2 text-base transition-colors rounded-lg hover:bg-white/[0.04] ${
                isActive(link.to)
                  ? 'text-white bg-white/[0.06]'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              {link.label}
            </Link>
          ))}

          <div className="ml-3 flex items-center gap-1 text-sm">
            <button
              type="button"
              onClick={() => setLanguage('pl')}
              aria-current={language === 'pl' ? 'true' : undefined}
              aria-label={t.nav.langPlAria}
              className={`px-1.5 py-1 transition-colors ${
                language === 'pl'
                  ? 'text-white font-medium'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              PL
            </button>
            <span aria-hidden="true" className="text-zinc-700 select-none">·</span>
            <button
              type="button"
              onClick={() => setLanguage('en')}
              aria-current={language === 'en' ? 'true' : undefined}
              aria-label={t.nav.langEnAria}
              className={`px-1.5 py-1 transition-colors ${
                language === 'en'
                  ? 'text-white font-medium'
                  : 'text-zinc-500 hover:text-zinc-300'
              }`}
            >
              EN
            </button>
          </div>

          <a
            href="https://spotto.pl"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-3 px-5 py-2 text-base font-medium text-white bg-brand-700 hover:bg-brand-600 border border-white/[0.08] hover:border-white/[0.14] rounded-lg transition-all"
          >
            {t.nav.spottoApp}
          </a>
        </div>

        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? t.nav.mobileToggleClose : t.nav.mobileToggleOpen}
          className="md:hidden p-2 text-zinc-400 hover:text-white"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-[#0a0a0f]/95 backdrop-blur-2xl border-b border-white/[0.06] overflow-hidden"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {links.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={handleMobileLinkClick}
                  aria-current={isActive(link.to) ? 'page' : undefined}
                  className={`px-4 py-3 rounded-lg hover:bg-white/[0.04] transition-colors ${
                    isActive(link.to)
                      ? 'text-white bg-white/[0.06]'
                      : 'text-zinc-300 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://spotto.pl"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 px-5 py-3 text-center font-medium text-white bg-brand-700 hover:bg-brand-600 border border-white/[0.08] rounded-lg"
              >
                {t.nav.spottoApp}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}
